import capabilityImagePreviewBack from "@/assets/figma/chat-add-menu/capability-image-preview-back.png";
import capabilityImagePreviewFront from "@/assets/figma/chat-add-menu/capability-image-preview-front.png";
import capabilityImagePreviewSide from "@/assets/figma/chat-add-menu/capability-image-preview-side.png";
import capabilityVideoPreview from "@/assets/figma/chat-add-menu/capability-video-preview.png";
import toolDailyOpportunity from "@/assets/figma/chat-add-menu/tool-daily-opportunity.svg";
import toolPromotionCalendar from "@/assets/figma/chat-add-menu/tool-promotion-calendar.svg";
import toolQuickTest from "@/assets/figma/chat-add-menu/tool-quick-test.svg";
import toolStoreData from "@/assets/figma/chat-add-menu/tool-store-data.svg";
import type { MyFilesResource } from "@/features/my-files/myFilesSchemas";
import type { MerryChatAddMenuCatalog } from "@/shared/ui/MerryChatAddMenu";

const capabilities: MerryChatAddMenuCatalog["capabilities"] = [
  {
    description: ["让卖点变成", "带货短片"],
    id: "marketing-video",
    kind: "video",
    label: "营销视频",
    previewImages: [capabilityVideoPreview],
  },
  {
    description: ["设计专业的", "运营图片"],
    id: "product-material",
    kind: "image",
    label: "商品素材",
    previewImages: [
      capabilityImagePreviewBack,
      capabilityImagePreviewSide,
      capabilityImagePreviewFront,
    ],
  },
];

const tools: MerryChatAddMenuCatalog["tools"] = [
  {
    description: "掌握店铺经营趋势，定位增长与风险",
    icon: toolStoreData,
    id: "store-data",
    label: "店铺数据",
  },
  {
    description: "每天发现值得跟进的商品机会",
    icon: toolDailyOpportunity,
    id: "daily-opportunity",
    label: "每日机会",
  },
  {
    description: "提供链接或上传图片，生成单品生意计划书",
    icon: toolQuickTest,
    id: "quick-test",
    label: "快速测品",
  },
  {
    description: "获取本店备战建议",
    icon: toolPromotionCalendar,
    id: "promotion-calendar",
    label: "大促日历",
  },
];

export interface BuildMerryChatAddMenuCatalogInput {
  allResources: ReadonlyArray<MyFilesResource>;
  hasMoreAllResources: boolean;
  previewUrls: Readonly<Record<string, string>>;
  visibleResources: ReadonlyArray<MyFilesResource>;
}

function countLabel(count: number, hasMore: boolean) {
  return `${count}${hasMore ? "+" : ""} 文件`;
}

/**
 * 生产组合菜单只把稳定 resource_id 带进视图；短期预览 URL 保持调用方本地状态。
 */
export function buildMerryChatAddMenuCatalog({
  allResources,
  hasMoreAllResources,
  previewUrls,
  visibleResources,
}: BuildMerryChatAddMenuCatalogInput): MerryChatAddMenuCatalog {
  const resourceCounts = allResources.reduce(
    (counts, resource) => ({
      ...counts,
      [resource.resourceType]: counts[resource.resourceType] + 1,
    }),
    { file: 0, image: 0, video: 0 },
  );

  return {
    assets: visibleResources.slice(0, 8).map((resource) => ({
      id: resource.resourceId,
      kind: "resource" as const,
      label: resource.displayName,
      previewUrl: previewUrls[resource.resourceId],
      resourceType: resource.resourceType,
    })),
    capabilities,
    categories: [
      {
        countLabel: countLabel(allResources.length, hasMoreAllResources),
        id: "all",
        kind: "all",
        label: "全部",
        previewImage: previewUrls[allResources[0]?.resourceId ?? ""],
      },
      {
        countLabel: countLabel(resourceCounts.file, hasMoreAllResources),
        id: "files",
        kind: "file",
        label: "文件",
      },
      {
        countLabel: countLabel(resourceCounts.image, hasMoreAllResources),
        id: "images",
        kind: "image",
        label: "图片",
        previewImage: previewUrls[
          allResources.find((resource) => resource.resourceType === "image")
            ?.resourceId ?? ""
        ],
      },
      {
        countLabel: countLabel(resourceCounts.video, hasMoreAllResources),
        id: "videos",
        kind: "video",
        label: "视频",
        previewImage: previewUrls[
          allResources.find((resource) => resource.resourceType === "video")
            ?.resourceId ?? ""
        ],
      },
    ],
    tools,
  };
}

export const emptyMerryChatAddMenuCatalog: MerryChatAddMenuCatalog = {
  assets: [],
  capabilities,
  categories: [
    { countLabel: "0 文件", id: "all", kind: "all", label: "全部" },
    { countLabel: "0 文件", id: "files", kind: "file", label: "文件" },
    { countLabel: "0 文件", id: "images", kind: "image", label: "图片" },
    { countLabel: "0 文件", id: "videos", kind: "video", label: "视频" },
  ],
  tools,
};
